import { useAuth } from '@agentnet/auth';
import { DataTable } from '@fluentsms/agentnet-web-components';
import { Box, Button, Grid, Typography, useTheme } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AddCircle } from '@material-ui/icons';
import { ColDef, GridReadyEvent, RowClassParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import {
  getOracleDowntimeSchedules,
  getOracleDowntimeTypes,
  deleteOracleDowntimeSchedule,
  OracleDowntimeType,
  DeleteOracleDowntimeScheduleRequest,
} from 'api/invoicing/oracle-downtime-api';
import clsx from 'clsx';
import OracleDowntimeDrawer from './components/OracleDowntimeDrawer';
import OrderManagementUnauthorizedAccessContent from 'core/layout-core/core-content/OrderManagement/OrderManagementUnauthorizedAccessContent';
import useAsync from 'hooks/useAsync';
import { useNavPermissions } from 'hooks/useNavPermissions';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import NoResults from 'ui-kit/components/noResults/NoResults';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { getOracleDowntimeColDefs } from './oracleDowntimeConfig';
import { OracleDowntimeSchedule, OracleDowntimeScheduleDisplay } from './types';
import { hasScheduleStarted } from './utility';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableDefault: {
      height: 'calc(100vh - 296px) !important',
      fontFamily: theme.typography.fontFamily,
    },
  }),
);

const OracleDowntimeList: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { getAccessToken } = useAuth();
  const { addSnackbarMessage } = useSnackBars();
  const { hasPermission } = useNavPermissions();
  const gridRef = useRef<AgGridReact>(null);
  const [rowData, setRowData] = useState<OracleDowntimeScheduleDisplay[]>([]);
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [selectedSchedule, setSelectedSchedule] = useState<OracleDowntimeScheduleDisplay | undefined>(undefined);
  const [downtimeTypes, setDowntimeTypes] = useState<OracleDowntimeType[]>([]);

  const fetchSchedules = useCallback(async (): Promise<OracleDowntimeSchedule[]> => {
    const token = await getAccessToken();
    return await getOracleDowntimeSchedules(token);
  }, [getAccessToken]);

  const { execute, status, value, errors } = useAsync<OracleDowntimeSchedule[]>(fetchSchedules, false);

  useEffect(() => {
    execute();

    const fetchTypes = async () => {
      try {
        const token = await getAccessToken();
        const types = await getOracleDowntimeTypes(token);
        setDowntimeTypes(types);
      } catch (error) {
        console.error('Error fetching downtime types:', error);
        setDowntimeTypes([]); // Explicitly set to empty to reflect error state
        addSnackbarMessage({
          message: 'Failed to load downtime types. Please refresh the page.',
          type: 'error',
        });
      }
    };
    fetchTypes();
  }, [execute, getAccessToken, addSnackbarMessage]);

  useEffect(() => {
    if (status === 'success' && value) {
      const now = new Date();
      const mappedData: OracleDowntimeScheduleDisplay[] = value.map((schedule) => ({
        id: schedule.id,
        typeCdId: schedule.typeCdId,
        type: schedule.typeDescription,
        startDateTime: new Date(schedule.startDateTime),
        endDateTime: schedule.endDateTime ? new Date(schedule.endDateTime) : null,
        createdBy: schedule.createdBy,
        createdDate: new Date(schedule.createdDate),
        modifiedBy: schedule.modifiedBy,
        modifiedDate: schedule.modifiedDate ? new Date(schedule.modifiedDate) : null,
        updTS: schedule.updTS,
        isPast: new Date(schedule.startDateTime) < now,
      }));

      // Sort: Future records before past, then by Start Date ascending
      const sortedData = mappedData.sort((a, b) => {
        // Future records (isPast=false) before past records (isPast=true)
        if (a.isPast !== b.isPast) {
          return a.isPast ? 1 : -1;
        }
        // Within same group (future or past), sort by Start Date ascending
        return a.startDateTime.getTime() - b.startDateTime.getTime();
      });

      setRowData(sortedData);
    }
  }, [status, value]);

  useEffect(() => {
    if (status === 'error' && errors?.length) {
      errors.forEach((err) => {
        addSnackbarMessage({
          message: err,
          type: 'error',
        });
      });
    }
  }, [status, errors, addSnackbarMessage]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      floatingFilter: true,
      filter: true,
      sortable: true,
      resizable: true,
      editable: false,
      suppressMenu: true,
      filterParams: { closeOnApply: true, suppressAndOrCondition: true },
    }),
    [],
  );

  const handleEditSchedule = useCallback((schedule: OracleDowntimeScheduleDisplay) => {
    setSelectedSchedule(schedule);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }, []);

  const handleDeleteSchedule = useCallback(
    async (schedule: OracleDowntimeScheduleDisplay) => {
      // Validate that the schedule has not started yet
      if (hasScheduleStarted(schedule)) {
        addSnackbarMessage({
          message: 'Cannot delete a schedule that has already started or is in the past.',
          type: 'error',
        });
        return;
      }

      try {
        const token = await getAccessToken();
        const payload: DeleteOracleDowntimeScheduleRequest = {
          OracleDowntimeScheduleId: schedule.id,
        };
        await deleteOracleDowntimeSchedule(payload, token);
        execute();
      } catch (error) {
        const apiError = error as { errorCode?: number; messages?: string[] };
        if (
          apiError.errorCode === 409 ||
          apiError.messages?.some((m: string) => m.toLowerCase().includes('concurrency'))
        ) {
          addSnackbarMessage({
            message: 'This schedule was modified by another user. The data will refresh with the latest version.',
            type: 'warning',
          });
          execute();
        } else {
          addSnackbarMessage({
            message: apiError.messages?.join(', ') || 'Failed to delete Oracle Downtime Schedule',
            type: 'error',
          });
        }
      }
    },
    [getAccessToken, execute, addSnackbarMessage],
  );

  const columnDefs = useMemo(
    () => getOracleDowntimeColDefs(handleEditSchedule, handleDeleteSchedule),
    [handleEditSchedule, handleDeleteSchedule],
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    if (params.api) {
      setDisplayedRowCount(params.api.getDisplayedRowCount());
    }
  }, []);

  const onFilterChanged = useCallback(() => {
    const api = gridRef.current?.api;
    if (api) {
      const count = api.getDisplayedRowCount();
      setDisplayedRowCount(count);

      if (count === 0) {
        api.showNoRowsOverlay();
      } else {
        api.hideOverlay();
      }
    }
  }, []);

  const getRowStyle = useCallback(
    (params: RowClassParams<OracleDowntimeScheduleDisplay>) => {
      if (params.data?.isPast) {
        return {
          backgroundColor: theme.palette.grey[100],
          opacity: 0.7,
          color: theme.palette.grey[600],
        };
      }
      return undefined;
    },
    [theme],
  );

  // Check if user has Order Management Admin permission (after all hooks per React Rules)
  if (!hasPermission?.orderManagementAdmin) {
    return <OrderManagementUnauthorizedAccessContent />;
  }

  const CustomOverlayLoading = () => {
    return (
      <div className="files-container">
        <div className="no-rows">
          <LoadingSpinner status="pending" className="files-container--spinner-root" />
        </div>
      </div>
    );
  };

  const CustomOverlayNoRows = () => {
    if (!rowData || rowData.length === 0) {
      return (
        <NoResults
          showButton={false}
          type="results"
          titleText="No Schedules Found"
          pText="There are currently no Oracle downtime schedules to display."
        />
      );
    } else {
      return (
        <NoResults
          showButton={false}
          type="results"
          titleText="No Results Found"
          pText="Try adjusting your filters to find what you're looking for."
        />
      );
    }
  };

  const handleScheduleDowntime = useCallback(() => {
    setSelectedSchedule(undefined);
    setDrawerMode('create');
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedSchedule(undefined);
  }, []);

  const handleDrawerSuccess = useCallback(() => {
    setDrawerOpen(false);
    setSelectedSchedule(undefined);
    execute();
  }, [execute]);

  const renderHeaderButtons = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<AddCircle />}
        onClick={handleScheduleDowntime}
        disabled={downtimeTypes.length === 0}
        data-testid="schedule-downtime-button"
      >
        Schedule Downtime
      </Button>
    );
  };

  if (status === 'pending') {
    return <CustomOverlayLoading />;
  }

  return (
    <ContentContainer fullWidth>
      <PageHeader
        title="Oracle Downtime Schedule"
        subtitle="View and manage scheduled Oracle downtime to prevent Order Management processing during system unavailability. Review upcoming maintenance windows and access a history of past downtime events."
        divider={false}
        contentRight={renderHeaderButtons()}
      />
      <Box px={3} mt={2}>
        <div className={clsx('ag-theme-alpine', classes.tableDefault)} data-testid="oracle-downtime-grid">
          <DataTable
            onGridReady={onGridReady}
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            gridOptions={{ suppressRowClickSelection: true }}
            onFilterChanged={onFilterChanged}
            animateRows={true}
            getRowStyle={getRowStyle}
            components={{
              customOverlayNoRows: CustomOverlayNoRows,
            }}
            noRowsOverlayComponent="customOverlayNoRows"
          />
        </div>
      </Box>
      <OracleDowntimeDrawer
        open={drawerOpen}
        mode={drawerMode}
        schedule={selectedSchedule}
        types={downtimeTypes}
        onClose={handleDrawerClose}
        onSuccess={handleDrawerSuccess}
      />
      <Box py={2} px={3} display="flex" justifyContent="space-between" width="100%">
        <Grid>
          <Typography variant="body2" color="textPrimary">
            Showing {displayedRowCount} of {rowData.length} schedules
          </Typography>
        </Grid>
      </Box>
    </ContentContainer>
  );
};

export default OracleDowntimeList;
